import { isUndefined, debounce, isEmpty } from 'lodash';
import { hashCode, isUserLoggedIn } from '../../util/helpers';
import { batchTranslate } from '../../api/translation';
const isInit = false;
const subscribers = [];
const TRANSLATIONS_CACHE_KEY = 'translations';
const TIMESTAMP_CACHE_KEY = `${TRANSLATIONS_CACHE_KEY}-timestamp`;
const TRANSLATION_CACHE_KEY = 'tran-cache-id';
let cache = null;
const translationQueue = new Set();
const accountConfig = window.AccountConfig;
/**
 * gets an insecure hash of the untranslated phrase
 * @param {string} text the untranslated phrase to be hashed
 * @returns {string}
 */
const getPhraseHash = (phrase) => {
    return hashCode(phrase);
};
const phrasesToPhraseRecords = (phrases) => {
    const phraseRecords = {};
    phrases.forEach((phrase) => {
        phraseRecords[getPhraseHash(phrase)] = phrase;
    });
    return phraseRecords;
};
const getTranslationCacheId = () => {
    return localStorage.getItem(TRANSLATION_CACHE_KEY);
};
/**
 * returns an insecure hash of all data points involved in caching translations for a given user
 * @returns
 */
const getCurrentUserTranslationCacheId = () => {
    const { Emp } = window.AccountConfig;
    const companyId = Emp.Company?.UID || '';
    const employeeId = Emp.UID || '';
    const languageId = Emp.Language.toString();
    return hashCode(companyId + employeeId + languageId);
};
const setTranslationCacheId = (id) => {
    localStorage.setItem(TRANSLATION_CACHE_KEY, id);
};
const setTranslationCacheTimestamp = (date) => {
    window.localStorage.setItem(TIMESTAMP_CACHE_KEY, date.toISOString());
};
const setTranslationCache = (translations) => {
    cache = translations;
    window.localStorage.setItem(TRANSLATIONS_CACHE_KEY, JSON.stringify(translations));
};
const flushCachedTranslations = () => {
    window.localStorage.setItem(TRANSLATIONS_CACHE_KEY, '{}');
};
/**
 * checks whether new custom translations have been added for the user's company
 * @returns {boolean}
 */
const hasGoodCompanyTranslationCacheTimestamp = () => {
    // No need to worry about company translations if language update time not specified
    if (isUndefined(accountConfig?.Company_LanguageUpdateTime)) {
        return true;
    }
    const timestamp = window.localStorage.getItem(TIMESTAMP_CACHE_KEY) || new Date().toISOString();
    const lastClientUpdate = new Date(timestamp);
    const lastServerUpdate = new Date(accountConfig.Company_LanguageUpdateTime);
    return lastClientUpdate > lastServerUpdate;
};
/**
 * Determines whether translation cache should be flushed for the current logged in user
 * @returns
 */
const shouldResetTranslationCache = () => {
    if (!isUserLoggedIn()) {
        return false;
    }
    const hasTranslationCacheIdMismatch = getTranslationCacheId() !== getCurrentUserTranslationCacheId();
    return !hasGoodCompanyTranslationCacheTimestamp() || hasTranslationCacheIdMismatch;
};
/**
 * flushes cached translations, timestamp, and user cache id
 * @returns
 */
const resetTranslationCache = () => {
    flushCachedTranslations();
    setTranslationCacheTimestamp(new Date());
    setTranslationCacheId(getCurrentUserTranslationCacheId());
};
/**
 * adds a new batch of translations to translation storage
 * @param translations
 */
const addTranslationBatchToCache = (translations) => {
    setTranslationCache({ ...cache, ...translations });
};
/**
 * load localstorage cache into memory
 */
const loadTranslationCache = () => {
    const storageJSON = window.localStorage.getItem(TRANSLATIONS_CACHE_KEY) || '{}';
    cache = JSON.parse(storageJSON);
};
/**
 * retrieves a single translation from translation storage
 * @returns {object}
 */
const getCachedTranslation = (phrase) => {
    const hash = getPhraseHash(phrase);
    return cache[hash];
};
/**
 * notify subscribers when new translations are added to cache
 * @param translationRecords
 */
const publishTranslations = (translationRecords) => {
    subscribers.forEach((callback) => {
        callback(translationRecords);
    });
};
/**
 * fetch translations for all phrases in translationQueue
 * @returns {Promise<Record<string,string>>}
 */
// eslint-disable-next-line consistent-return
const _loadTranslations = async () => {
    const phrases = [...translationQueue];
    if (phrases.length === 0) {
        return Promise.resolve({});
    }
    translationQueue.clear();
    const translationRecords = await batchTranslate({
        Phrases: phrasesToPhraseRecords(phrases)
    })
        .then((resp) => resp.json())
        .then((json) => json.d);
    if (!isEmpty(translationRecords)) {
        addTranslationBatchToCache(translationRecords);
        publishTranslations(translationRecords);
    }
};
/**
 * debounced (50ms) call to _loadTranslations
 */
const loadTranslations = debounce(_loadTranslations, 50);
/**
 * add translation to queue and schedule fetch of all queued translations
 * @param phrase
 */
const queueTranslation = (phrase) => {
    translationQueue.add(phrase);
    loadTranslations();
};
/**
 * subscribe to receive updates when new translations are added to cache
 * @param callback
 */
const onTranslationUpdate = (callback) => {
    subscribers.push(callback);
};
export const useTranslationStore = () => {
    if (!isInit) {
        if (shouldResetTranslationCache()) {
            resetTranslationCache();
        }
        loadTranslationCache();
    }
    return {
        addTranslationBatchToCache,
        getCachedTranslation,
        getPhraseHash,
        queueTranslation,
        onTranslationUpdate
    };
};
